import { Box, Stack } from "@mui/material";
import React, { useEffect, useState } from "react";
import {
    DividerMargined,
    ListLocationItemsContainer,
    ResultHeadlineTypography,
} from "./LocationSearchTab.styles";
import { LocationSearchBar } from "./LocationSearchBar";
import ListItemLocation from "../../../SearchHeader/ListItem/ListItemLocation";
import {
    LocationListItemType,
    LocationType,
} from "../../../SearchHeader/ListItem/ListItemLocation.types";
import debounce from "lodash/debounce";
import GooglePlaceService, {
    getPlaceDetails,
} from "../../../../../Infrastructure/Services/GooglePlaceService";
import { SearchItemGroupClickArgType } from "../../../SearchHeader/SearchTextField.types";
import { SearchPlacesSearchParams } from "./SearchPlaces.types";
import { DEFAULT_DEBOUNCE_MILLISECONDS } from "../../../SearchHeader/SearchTextField";
import { LocationPermissionMissingModal } from "../../../Modals/ServiceModals/LocationPermissionMissingModal";
import {
    DEVICE_LOCATION_AUTHORIZATION_STATES,
    initialDeviceLocationAuthorization,
    LOCATION_AUTHORIZATION_STATES,
    useNativeApp,
} from "@infrastructure/Providers/NativeAppProvider";
import {
    triggerAndroidCheckForLocationPermissions,
    triggerCheckForGeneralLocationPermissions,
    triggerNativeLocationAccess,
} from "@infrastructure/Helpers/NativeUserLocationAccessTrigger";
import {
    DEVICE_TYPES,
    useDeviceType,
} from "@infrastructure/Hooks/useDeviceType";
import { CommunityCircularProgress } from "../../../../Shared/styles";

export function LocationSearchTab(props: {
    setTabIndex: (newIndex: number) => void;
    setUserLocation: (userLocation: LocationType) => void;
}) {
    const [places, setPlaces] = useState<LocationListItemType[]>([]);
    const [isRequestingUserLocation, setIsRequestingUserLocation] =
        useState(false);
    const groupId = undefined;
    const [resultHeadline, setResultHeadline] = useState("");
    const [userLocation, setUserLocation] = useState<GeolocationPosition>();
    const [
        showLocationPermissionMissingModal,
        setShowLocationPermissionMissingModal,
    ] = useState(false);

    const [userLocationPermissionState, setUserLocationPermissionState] =
        useState(LOCATION_PERMISSION_STATE.INITIAL);
    const { isLocationAuthorized, isDeviceLocationAuthorized } = useNativeApp();
    const { deviceType } = useDeviceType();

    useEffect(() => {
        if (userLocationPermissionState === LOCATION_PERMISSION_STATE.GRANTED) {
            searchUsingLocation();
        }
    }, [userLocationPermissionState]);

    useEffect(() => determineUserAddress(), []);

    useEffect(() => {
        if (
            userLocationPermissionState ===
                LOCATION_PERMISSION_STATE.REQUESTING &&
            "geolocation" in navigator
        ) {
            if (deviceType === DEVICE_TYPES.BROWSER) {
                // assume permission is granted and let everything be handled by the regular process
                setUserLocationPermissionState(
                    LOCATION_PERMISSION_STATE.GRANTED,
                );
                handleLocationFlags(
                    deviceType,
                    isRequestingUserLocation,
                    isLocationAuthorized,
                    LOCATION_PERMISSION_STATE.GRANTED,
                );
            } else {
                navigator.geolocation.getCurrentPosition(
                    (location) => {
                        if (location) {
                            setUserLocationPermissionState(
                                LOCATION_PERMISSION_STATE.GRANTED,
                            );
                            handleLocationFlags(
                                deviceType,
                                isRequestingUserLocation,
                                isLocationAuthorized,
                                LOCATION_PERMISSION_STATE.GRANTED,
                            );
                        }
                    },
                    () => {
                        if (
                            deviceType ===
                                DEVICE_TYPES.ANDROID_WITHOUT_PLUGIN ||
                            deviceType === DEVICE_TYPES.IOS_WITHOUT_PLUGIN
                        ) {
                            setShowLocationPermissionMissingModal(true);
                            setUserLocationPermissionState(
                                LOCATION_PERMISSION_STATE.DENIED,
                            );
                            setIsRequestingUserLocation(false);
                        } else if (
                            deviceType === DEVICE_TYPES.IOS_WITH_PLUGIN
                        ) {
                            if (
                                isLocationAuthorized ===
                                    LOCATION_AUTHORIZATION_STATES.GRANTED &&
                                isDeviceLocationAuthorized ===
                                    DEVICE_LOCATION_AUTHORIZATION_STATES.GRANTED
                            ) {
                                setUserLocationPermissionState(
                                    LOCATION_PERMISSION_STATE.GRANTED,
                                );
                                handleLocationFlags(
                                    deviceType,
                                    isRequestingUserLocation,
                                    isLocationAuthorized,
                                    LOCATION_PERMISSION_STATE.GRANTED,
                                );
                            } else {
                                setUserLocationPermissionState(
                                    LOCATION_PERMISSION_STATE.DENIED,
                                );
                                handleLocationFlags(
                                    deviceType,
                                    isRequestingUserLocation,
                                    isLocationAuthorized,
                                    LOCATION_PERMISSION_STATE.DENIED,
                                );
                            }
                        } else if (
                            deviceType === DEVICE_TYPES.ANDROID_WITH_PLUGIN &&
                            isLocationAuthorized ===
                                LOCATION_AUTHORIZATION_STATES.GRANTED
                        ) {
                            setUserLocationPermissionState(
                                LOCATION_PERMISSION_STATE.DENIED,
                            );
                            handleLocationFlags(
                                deviceType,
                                isRequestingUserLocation,
                                isLocationAuthorized,
                                LOCATION_PERMISSION_STATE.DENIED,
                            );
                        }
                    },
                    {
                        maximumAge: 0,
                        timeout: 3000,
                    },
                );
            }
        } else {
            handleLocationFlags(
                deviceType,
                isRequestingUserLocation,
                isLocationAuthorized,
                userLocationPermissionState,
            );
        }
    }, [
        userLocationPermissionState,
        isDeviceLocationAuthorized,
        isLocationAuthorized,
        isRequestingUserLocation,
    ]);

    const selectAddressHandler = (args: SearchItemGroupClickArgType) => {
        getPlaceDetails(args.location.id.toString()).then((placeDetails) =>
            props.setUserLocation({
                ...args.location,
                latitude: placeDetails.lat,
                longitude: placeDetails.lng,
            }),
        );

        handleClose();
    };

    function searchUsingLocation() {
        navigator.geolocation.getCurrentPosition(
            (location) => {
                if (location) {
                    setUserLocation(location);
                    debounceSearchPlaces("", () => {}, {
                        location: location,
                    });
                }
            },
            () => {
                setShowLocationPermissionMissingModal(true);
                setUserLocationPermissionState(
                    LOCATION_PERMISSION_STATE.DENIED,
                );
                setIsRequestingUserLocation(false);
            },
        );
    }

    function handleLocationFlags(
        deviceType: DEVICE_TYPES,
        isRequestingUserLocation: boolean,
        isLocationAuthorized: LOCATION_AUTHORIZATION_STATES,
        userLocationPermissionState: LOCATION_PERMISSION_STATE,
    ) {
        if (
            deviceType === DEVICE_TYPES.ANDROID_WITH_PLUGIN &&
            isRequestingUserLocation
        ) {
            if (
                isLocationAuthorized ===
                    LOCATION_AUTHORIZATION_STATES.GRANTED &&
                isDeviceLocationAuthorized ===
                    DEVICE_LOCATION_AUTHORIZATION_STATES.GRANTED &&
                userLocationPermissionState ===
                    LOCATION_PERMISSION_STATE.GRANTED
            ) {
                searchUsingLocation();
            } else if (
                userLocationPermissionState ===
                    LOCATION_PERMISSION_STATE.DENIED &&
                isLocationAuthorized === LOCATION_AUTHORIZATION_STATES.GRANTED
            ) {
                triggerCheckForGeneralLocationPermissions();
                setIsRequestingUserLocation(false);
                if (
                    initialDeviceLocationAuthorization(deviceType) ===
                    DEVICE_LOCATION_AUTHORIZATION_STATES.GRANTED
                ) {
                    setShowLocationPermissionMissingModal(true);
                }
            } else {
                triggerAndroidCheckForLocationPermissions();
                setIsRequestingUserLocation(false);
            }
        } else if (
            deviceType === DEVICE_TYPES.BROWSER &&
            userLocationPermissionState === LOCATION_PERMISSION_STATE.GRANTED &&
            isRequestingUserLocation
        ) {
            searchUsingLocation();
        } else if (
            deviceType === DEVICE_TYPES.IOS_WITH_PLUGIN &&
            isRequestingUserLocation
        ) {
            if (
                isDeviceLocationAuthorized ===
                    DEVICE_LOCATION_AUTHORIZATION_STATES.DENIED ||
                isDeviceLocationAuthorized ===
                    DEVICE_LOCATION_AUTHORIZATION_STATES.UNDEFINED
            ) {
                triggerCheckForGeneralLocationPermissions();
                setIsRequestingUserLocation(false);
            } else if (
                userLocationPermissionState === LOCATION_PERMISSION_STATE.DENIED
            ) {
                triggerNativeLocationAccess();
                setIsRequestingUserLocation(false);
            } else {
                setIsRequestingUserLocation(false);
                searchUsingLocation();
            }
        } else if (
            deviceType === DEVICE_TYPES.IOS_WITHOUT_PLUGIN &&
            userLocationPermissionState === LOCATION_PERMISSION_STATE.GRANTED
        ) {
            searchUsingLocation();
        } else if (
            deviceType === DEVICE_TYPES.ANDROID_WITHOUT_PLUGIN &&
            userLocationPermissionState === LOCATION_PERMISSION_STATE.GRANTED
        ) {
            searchUsingLocation();
        }
    }

    const debounceSearchPlaces = debounce(
        (
            value: string,
            dispatch: Function,
            searchParameters: SearchPlacesSearchParams,
        ) => {
            if (!value || value.length >= 3) {
                const { location = undefined, groupId = undefined } =
                    searchParameters;
                setIsRequestingUserLocation(false);
                GooglePlaceService.searchForPlaces(
                    value,
                    location ?? userLocation,
                    groupId?.toString(),
                )
                    .then((data: LocationType[]) => {
                        return data.map((location: LocationType) => {
                            return {
                                onItemClick: selectAddressHandler,
                                location: location,
                            } as LocationListItemType;
                        });
                    })
                    .then((data: LocationListItemType[]) => {
                        if (!data.length) {
                            setResultHeadline("");
                        } else if (value) {
                            setResultHeadline("Vorschläge");
                        } else {
                            setResultHeadline("Vorschläge aus dem Umkreis");
                        }
                        setPlaces(data);
                    })
                    .catch((err) => {
                        console.error(err);
                    });
            }
        },
        DEFAULT_DEBOUNCE_MILLISECONDS,
    );

    function determineUserAddress() {
        if (
            deviceType === DEVICE_TYPES.ANDROID_WITH_PLUGIN &&
            isLocationAuthorized ===
                LOCATION_AUTHORIZATION_STATES.NOT_DETERMINED
        ) {
            triggerAndroidCheckForLocationPermissions();
            return;
        }

        if (isLocationAuthorized === LOCATION_AUTHORIZATION_STATES.DENIED) {
            triggerNativeLocationAccess();
            return;
        }

        if (
            isDeviceLocationAuthorized ===
            DEVICE_LOCATION_AUTHORIZATION_STATES.DENIED
        ) {
            triggerCheckForGeneralLocationPermissions();
            return;
        }

        setIsRequestingUserLocation(true);
        setUserLocationPermissionState(LOCATION_PERMISSION_STATE.REQUESTING);
    }

    function handleClose() {
        props.setTabIndex(0);
        setUserLocationPermissionState(LOCATION_PERMISSION_STATE.INITIAL);
    }

    return (
        <>
            <Box padding={"12px 16px"}>
                <LocationSearchBar
                    debouncedSearch={debounceSearchPlaces}
                    groupId={groupId}
                    determineUserAddress={determineUserAddress}
                />
            </Box>
            <div style={{ height: "calc(100% - 64px)", overflowY: "auto" }}>
                <Stack
                    direction="row"
                    justifyContent="center"
                    style={{ margin: "0 16px 8px 16px" }}
                >
                    <ResultHeadlineTypography
                        style={{
                            marginRight: isRequestingUserLocation ? "16px" : 0,
                        }}
                    >
                        {resultHeadline}
                    </ResultHeadlineTypography>
                    {isRequestingUserLocation && <CommunityCircularProgress />}
                </Stack>

                <ListLocationItemsContainer>
                    {places.map((locationItem: LocationListItemType, index) => (
                        <React.Fragment key={index}>
                            <ListItemLocation
                                key={index}
                                onItemClick={locationItem.onItemClick}
                                location={locationItem.location}
                            />
                            {index < places.length - 1 && places.length > 1 && (
                                <DividerMargined variant="inset" />
                            )}
                        </React.Fragment>
                    ))}
                </ListLocationItemsContainer>
            </div>
            <LocationPermissionMissingModal
                isOpen={showLocationPermissionMissingModal}
                handleClose={() => setShowLocationPermissionMissingModal(false)}
            />
        </>
    );
}

export enum LOCATION_PERMISSION_STATE {
    GRANTED = "granted",
    DENIED = "denied",
    PROMPT = "prompt",
    REQUESTING = "requesting",
    INITIAL = "initial",
}
